import { ChangeDetectionStrategy, Component, computed, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { ConfigSchema, FieldDefinition } from '../models/provider.model';
import { ConfigViolationCode, fieldProblem, humanizeKey, schemaFields } from '../util/config-schema.util';
import { descriptorText } from '../util/descriptor-text';
import { LanguageService } from '../../../core/i18n/language.service';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { NumberInput } from '../../../shared/components/forms/number-input';
import { Toggle } from '../../../shared/components/forms/toggle';
import { Select, SelectOption } from '../../../shared/components/forms/select';

/** A field as rendered: its definition plus translated texts. */
interface FieldView {
  def: FieldDefinition;
  label: string;
  help: string | null;
  options: SelectOption[];
}

interface SectionView {
  key: string;
  title: string;
  description: string | null;
  fields: FieldView[];
}

/**
 * Feed (or subscription) configuration form built from a provider's descriptor schema, for
 * provider types without a dedicated UI: one card per section in descriptor order, one control per
 * field type (`secret` → password input), canonical values written (`true`/`false`, plain digits,
 * exact enum values; an empty value is removed so the provider's default applies).
 *
 * Texts come from translation keys built from codes, since a descriptor carries none:
 * `providers.config.{type}.sections.{key}.title|description` and
 * `providers.config.{type}.fields.{key}.label|help|values.{value}`. A missing label falls back to
 * readable text from the code, a missing description/help is hidden, a missing enum value label
 * shows the value.
 *
 * Problems are checked client-side (`fieldProblem`, mirroring core) and shown once a field is
 * edited; `serverErrors` (core's 400 violations, by key) win until that field is edited again.
 * Stored keys the schema doesn't know are listed separately to be removed, since core rejects them.
 */
@Component({
  selector: 'app-config-schema-form',
  standalone: true,
  imports: [FormsModule, TranslocoDirective, FormField, TextInput, NumberInput, Toggle, Select],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-container *transloco="let t; read: 'providers.config'">
      <div class="flex flex-col gap-6">
        @for (section of sections(); track section.key) {
          <div class="rounded-card bg-card p-6 shadow-hairline">
            <div class="mb-1 font-display text-[12px] font-semibold uppercase tracking-wide text-ink-2">{{ section.title }}</div>
            @if (section.description) {
              <p class="m-0 text-[12px] leading-[18px] text-ink-3">{{ section.description }}</p>
            }
            <div class="mt-5 flex max-w-[560px] flex-col gap-5">
              @for (field of section.fields; track field.def.key) {
                <app-form-field [help]="field.help ?? ''" [error]="errorText(field.def)">
                  @switch (field.def.type) {
                    @case ('boolean') {
                      <label class="flex items-center gap-2 text-[13px] font-semibold text-ink-2">
                        <app-toggle
                          [ngModel]="booleanValue(field.def)"
                          (ngModelChange)="set(field.def.key, $event ? 'true' : 'false')"
                          [ngModelOptions]="{ standalone: true }"
                        ></app-toggle>
                        {{ field.label }}{{ field.def.required ? ' *' : '' }}
                      </label>
                    }
                    @case ('enum') {
                      <app-select
                        [label]="field.label + (field.def.required ? ' *' : '')"
                        [options]="field.options"
                        [clearable]="!field.def.required"
                        [placeholder]="placeholder(field.def)"
                        [invalid]="!!errorText(field.def)"
                        [ngModel]="value(field.def.key) ?? null"
                        (ngModelChange)="set(field.def.key, $event ?? '')"
                        [ngModelOptions]="{ standalone: true }"
                      ></app-select>
                    }
                    @case ('integer') {
                      <app-number-input
                        [label]="field.label + (field.def.required ? ' *' : '')"
                        [min]="field.def.min ?? null"
                        [max]="field.def.max ?? null"
                        [step]="1"
                        [placeholder]="placeholder(field.def)"
                        [invalid]="!!errorText(field.def)"
                        [ngModel]="numberValue(field.def)"
                        (ngModelChange)="set(field.def.key, $event === null ? '' : String($event))"
                        [ngModelOptions]="{ standalone: true }"
                      ></app-number-input>
                    }
                    @case ('number') {
                      <app-number-input
                        [label]="field.label + (field.def.required ? ' *' : '')"
                        [min]="field.def.min ?? null"
                        [max]="field.def.max ?? null"
                        step="any"
                        [placeholder]="placeholder(field.def)"
                        [invalid]="!!errorText(field.def)"
                        [ngModel]="numberValue(field.def)"
                        (ngModelChange)="set(field.def.key, $event === null ? '' : String($event))"
                        [ngModelOptions]="{ standalone: true }"
                      ></app-number-input>
                    }
                    @default {
                      <app-text-input
                        [label]="field.label + (field.def.required ? ' *' : '')"
                        [type]="field.def.secret ? 'password' : 'text'"
                        [placeholder]="placeholder(field.def)"
                        [invalid]="!!errorText(field.def)"
                        [ngModel]="value(field.def.key) ?? ''"
                        (ngModelChange)="set(field.def.key, $event)"
                        [ngModelOptions]="{ standalone: true }"
                      ></app-text-input>
                    }
                  }
                </app-form-field>
              }
            </div>
          </div>
        }

        @if (unknownKeys().length) {
          <div class="rounded-card bg-card p-6 shadow-hairline">
            <div class="mb-1 font-display text-[12px] font-semibold uppercase tracking-wide text-danger">{{ t('unknownKeys.title') }}</div>
            <p class="m-0 mb-4 text-[12px] leading-[18px] text-ink-3">{{ t('unknownKeys.help') }}</p>
            @for (key of unknownKeys(); track key) {
              <div class="flex items-center gap-3 border-b border-hairline py-2 last:border-b-0">
                <span class="font-num text-[12px] text-ink-1">{{ key }}</span>
                <span class="min-w-0 flex-1 truncate font-num text-[12px] text-ink-3">{{ values()[key] }}</span>
                <button type="button" (click)="remove(key)" class="text-[13px] font-semibold text-danger hover:underline">
                  {{ t('unknownKeys.remove') }}
                </button>
              </div>
            }
          </div>
        }
      </div>
    </ng-container>
  `,
})
export class ConfigSchemaFormComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);

  readonly schema = input.required<ConfigSchema>();
  /** The provider type, for building translation keys. */
  readonly providerType = input.required<string>();
  /** The configuration map being edited; empty values are removed. */
  readonly values = model.required<Record<string, string>>();
  /** Core's violations from the last save, by key. */
  readonly serverErrors = input<Record<string, ConfigViolationCode>>({});
  /** A field was edited (the parent drops that key's server error). */
  readonly fieldEdited = output<string>();

  protected readonly String = String;
  /** Fields edited since load: their client-side problems are shown. */
  private readonly touched = signal(new Set<string>());

  protected readonly sections = computed<SectionView[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const base = `providers.config.${this.providerType()}`;
    return this.schema().sections.map((section) => ({
      key: section.key,
      title: this.text(`${base}.sections.${section.key}.title`) ?? humanizeKey(section.key),
      description: this.text(`${base}.sections.${section.key}.description`),
      fields: section.fields.map((def) => ({
        def,
        label: this.text(`${base}.fields.${def.key}.label`) ?? humanizeKey(def.key),
        help: this.text(`${base}.fields.${def.key}.help`),
        options: (def.values ?? []).map((value) => ({
          value,
          label: this.text(`${base}.fields.${def.key}.values.${value}`) ?? value,
        })),
      })),
    }));
  });

  protected readonly unknownKeys = computed(() => {
    const known = new Set(schemaFields(this.schema()).map((f) => f.key));
    return Object.keys(this.values()).filter((key) => !known.has(key));
  });

  protected set(key: string, value: string): void {
    const next = { ...this.values() };
    if (value === '') {
      delete next[key];
    } else {
      next[key] = value;
    }
    this.values.set(next);
    this.touched.update((keys) => new Set(keys).add(key));
    this.fieldEdited.emit(key);
  }

  protected remove(key: string): void {
    this.set(key, '');
  }

  protected errorText(def: FieldDefinition): string | null {
    this.languageService.current();
    const code =
      this.serverErrors()[def.key] ?? (this.touched().has(def.key) ? fieldProblem(def, this.values()[def.key]) : null);
    if (!code) return null;
    return this.transloco.translate(`providers.config.errors.${code}`, {
      min: def.min,
      max: def.max,
      values: (def.values ?? []).join(', '),
    });
  }

  /** The default the provider applies when the field is empty. */
  protected placeholder(def: FieldDefinition): string {
    return def.default !== undefined ? this.transloco.translate('providers.config.defaultPlaceholder', { value: def.default }) : '';
  }

  /** A key's current value; undefined when it's absent (the provider's default applies). */
  protected value(key: string): string | undefined {
    return this.values()[key];
  }

  protected booleanValue(def: FieldDefinition): boolean {
    return (this.value(def.key) ?? def.default) === 'true';
  }

  protected numberValue(def: FieldDefinition): number | null {
    const value = this.value(def.key);
    return value === undefined || value === '' || Number.isNaN(Number(value)) ? null : Number(value);
  }

  private text(key: string): string | null {
    return descriptorText(this.transloco, key);
  }
}
